import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { ActiveRunPhase, ActiveRunSummary, Message, ReferenceItem, RunDetail, RunLiveSnapshot, TerminalRunHydration } from "../../api/types.js";
import type { UseSessionsState } from "../sessions/useSessions.js";
import { getActiveRun, getRunRecovery } from "./runApi.js";
import { mergeReferenceEntities } from "../sources/citationSources.js";
import { LIVE_RUN_EVENT_LIMIT, liveRunOwnsThread, mergeLiveCitationSources, mergeLiveMessages, type LiveRunBuffer } from "./liveRun.js";

const RUN_POLL_INTERVAL_MS = 1500;
const DISCOVERY_RETRY_MS = 5000;

export type BindLiveRunInput = {
  runId: string;
  sessionId: string;
  projectId: string | null;
  sessionTitle: string;
  phase: ActiveRunPhase;
  createdAt: string;
};

export type UseActiveRunState = {
  activeRun: ActiveRunSummary | null;
  recoveringRun: RunDetail | null;
  liveRun: LiveRunBuffer | null;
  visibleMessages: Message[];
  visibleCitationSources: ReferenceItem[];
  visibleEvents: unknown[];
  blockedByActiveRun: boolean;
  ownsVisibleRun: boolean;
  ownsVisibleStream: boolean;
  ownsVisibleStreamBuffer: boolean;
  ownsVisibleSnapshot: boolean;
  beginLiveRun: (buffer: LiveRunBuffer) => void;
  bindLiveRun: (input: BindLiveRunInput) => void;
  adoptActiveRun: (run: ActiveRunSummary) => void;
  discardPendingLiveRun: () => void;
  setLiveMessages: Dispatch<SetStateAction<Message[]>>;
  setLiveCitationSources: Dispatch<SetStateAction<ReferenceItem[]>>;
  setLiveEvents: Dispatch<SetStateAction<unknown[]>>;
  setPhase: (phase: ActiveRunPhase) => void;
  setSessionTitle: (sessionId: string, title: string) => void;
  detachLiveRunStream: () => void;
  markLiveRunSettled: () => void;
  recoverNow: () => Promise<void>;
  clearLiveRunAfterHydration: () => void;
};

const ACTIVE_PHASES: ActiveRunPhase[] = ["running", "cancelling", "finalizing"];

function isActivePhase(status: RunDetail["status"]): status is ActiveRunPhase {
  return ACTIVE_PHASES.includes(status as ActiveRunPhase);
}

export function useActiveRunState({ sessions }: { sessions: UseSessionsState }): UseActiveRunState {
  const [activeRun, setActiveRun] = useState<ActiveRunSummary | null>(null);
  const [recoveringRun, setRecoveringRun] = useState<RunDetail | null>(null);
  const [liveRun, setLiveRun] = useState<LiveRunBuffer | null>(null);
  const [pendingHydration, setPendingHydration] = useState(false);
  const [settledFailure, setSettledFailure] = useState(false);

  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  const liveRunRef = useRef(liveRun);
  liveRunRef.current = liveRun;
  const activeRunRef = useRef(activeRun);
  activeRunRef.current = activeRun;
  const recoveringRunRef = useRef(recoveringRun);
  recoveringRunRef.current = recoveringRun;
  const pendingHydrationRef = useRef(pendingHydration);
  pendingHydrationRef.current = pendingHydration;
  const pollInFlightRef = useRef(false);

  const currentSessionId = sessions.currentSession?.id || null;
  const pendingNewChatLive = Boolean(liveRun && liveRun.sessionId === null);
  const isWelcome = !sessions.currentSession && sessions.messages.length === 0 && !pendingNewChatLive;
  const ownsVisibleRun = liveRunOwnsThread({ currentSessionId, isWelcome, liveRun });
  const ownsVisibleStream = Boolean(ownsVisibleRun && liveRun?.source === "stream" && liveRun.abortController);
  const ownsVisibleStreamBuffer = Boolean(ownsVisibleRun && liveRun?.source === "stream");
  const ownsVisibleSnapshot = Boolean(ownsVisibleRun && liveRun?.source === "snapshot");

  const visibleMessages = useMemo(
    () => mergeLiveMessages({ persisted: sessions.messages, currentSessionId, isWelcome, liveRun }),
    [sessions.messages, currentSessionId, isWelcome, liveRun]
  );
  const visibleCitationSources = useMemo(
    () => mergeLiveCitationSources({ persisted: sessions.citationSources, currentSessionId, isWelcome, liveRun }),
    [sessions.citationSources, currentSessionId, isWelcome, liveRun]
  );
  const visibleEvents = ownsVisibleRun && liveRun ? liveRun.events : sessions.events;

  const blockedByActiveRun = Boolean(activeRun) || pendingHydration || (Boolean(liveRun) && !settledFailure);

  function abortRedundantLiveStream(runId: string) {
    // Once the terminal outcome is confirmed by the database, a still-open
    // SSE reader is redundant; aborting it releases the controller's run UI.
    const buffer = liveRunRef.current;
    if (buffer && buffer.runId === runId) buffer.abortController?.abort();
  }

  function settleLiveBufferFailed(detail: RunDetail) {
    setLiveRun((current) => {
      if (!current || (current.runId && current.runId !== detail.id)) return current;
      return {
        ...current,
        messages: current.messages.map((message) => message.role === "assistant"
          ? {
              ...message,
              content: message.content || `Truth-Seeker 回复未完成：${detail.error || "本次处理没有正常完成。"}`,
              meta: { ...(message.meta || {}), runPhase: "failed", processStatus: "failed" }
            }
          : message)
      };
    });
  }

  function applyLiveSnapshot(detail: RunDetail, snapshot: RunLiveSnapshot) {
    if (!isActivePhase(detail.status) || snapshot.runId !== detail.id || snapshot.sessionId !== detail.sessionId) return;
    const phase = detail.status;
    setSettledFailure(false);
    setLiveRun((current) => {
      if (current?.runId === detail.id && current.source === "stream" && current.abortController) return current;
      if (current?.runId === detail.id && Number(current.snapshotRevision || 0) >= snapshot.revision) return current;
      const optimisticUsers = current?.runId === detail.id
        ? current.messages.filter((message) => message.role === "user")
        : [];
      const createdAtMs = Date.parse(detail.createdAt);
      const runTrace = {
        ...snapshot.runTrace,
        elapsedMs: Math.max(
          snapshot.runTrace.elapsedMs,
          Number.isFinite(createdAtMs) ? Date.now() - createdAtMs : 0
        )
      };
      const assistant: Message = {
        id: `live-snapshot:${detail.id}`,
        role: "assistant",
        runId: detail.id,
        content: snapshot.content,
        meta: {
          contentParts: snapshot.contentParts,
          citationMap: snapshot.citationMap,
          runTrace,
          processStatus: runTrace.status,
          processElapsedMs: runTrace.elapsedMs,
          runPhase: phase,
          assistantSegmentId: snapshot.segmentId,
          liveSnapshotRevision: snapshot.revision
        }
      };
      return {
        runId: detail.id,
        sessionId: detail.sessionId,
        projectId: detail.projectId,
        phase,
        createdAt: detail.createdAt,
        messages: [...optimisticUsers, assistant],
        citationSources: snapshot.citationSources,
        events: snapshot.runTrace.items,
        abortController: null,
        source: "snapshot",
        snapshotRevision: snapshot.revision
      };
    });
  }

  async function applyRunDetail(detail: RunDetail, terminalHydration: TerminalRunHydration | null) {
    // A poll can land after the run was already resolved (for example a
    // recoverNow queued behind stale refs); ignore details nobody tracks.
    const stillTracked = liveRunRef.current?.runId === detail.id
      || activeRunRef.current?.runId === detail.id
      || recoveringRunRef.current?.id === detail.id;
    if (!stillTracked) return;
    const sessionsState = sessionsRef.current;
    const visibleSessionId = sessionsState.currentSession?.id || null;
    if (isActivePhase(detail.status)) {
      const phase = detail.status;
      setActiveRun((current) => {
        if (current && current.runId === detail.id) {
          return current.phase === phase ? current : { ...current, phase };
        }
        return {
          runId: detail.id,
          sessionId: detail.sessionId,
          projectId: detail.projectId,
          sessionTitle: sessionsState.sessions.find((session) => session.id === detail.sessionId)?.title || "",
          phase,
          createdAt: detail.createdAt
        };
      });
      setLiveRun((current) => current && current.runId === detail.id && current.phase !== phase ? { ...current, phase } : current);
      setRecoveringRun((current) => (current && current.id === detail.id ? detail : current));
      return;
    }
    if (detail.status === "failed" && !detail.assistantMessageId) {
      // The database claim is terminal, so sending unblocks; the detail is
      // retained only for the visible failure copy.
      setRecoveringRun(detail);
      setActiveRun(null);
      setPendingHydration(false);
      setSettledFailure(true);
      settleLiveBufferFailed(detail);
      abortRedundantLiveStream(detail.id);
      if (detail.sessionId === visibleSessionId && !liveRunRef.current) {
        void sessionsState.openSessionById(detail.sessionId, { immediate: true, scroll: "none" }).catch(() => undefined);
      }
      return;
    }
    setRecoveringRun(detail);
    if (!detail.assistantMessageId) {
      // completed/cancelled whose formal message has not landed yet: retain
      // the claim and keep polling until persistence finishes.
      setPendingHydration(true);
      return;
    }
    if (detail.sessionId === visibleSessionId) {
      setPendingHydration(true);
      const restored = terminalHydration
        ? sessionsState.restoreRunMessages(detail.sessionId, detail.assistantMessageId, terminalHydration)
        : false;
      if (!restored) return;
      abortRedundantLiveStream(detail.id);
      setLiveRun(null);
      setActiveRun(null);
      setRecoveringRun(null);
      setPendingHydration(false);
      sessionsState.invalidateReferencePool(detail.sessionId);
      void sessionsState.loadSessions().catch(() => undefined);
      void sessionsState.refreshReferencePoolSummary(detail.sessionId).catch(() => undefined);
      return;
    }
    // Terminal run confirmed background-only: release the claim locally and
    // refresh summaries without touching the visible conversation.
    abortRedundantLiveStream(detail.id);
    setLiveRun((current) => (current && current.runId === detail.id ? null : current));
    setActiveRun(null);
    setRecoveringRun(null);
    setPendingHydration(false);
    void sessionsState.loadSessions().catch(() => undefined);
  }

  function handleUntrackableRun(runId: string) {
    // The server does not know this run for this owner: release every claim
    // tied to it, but keep any streamed content visible without blocking.
    setActiveRun((current) => (current && current.runId === runId ? null : current));
    setRecoveringRun((current) => (current && current.id === runId ? null : current));
    setPendingHydration(false);
    const buffer = liveRunRef.current;
    if (buffer && buffer.runId === runId) {
      setSettledFailure(true);
      const sessionsState = sessionsRef.current;
      void sessionsState.loadSessions().catch(() => undefined);
      if (buffer.sessionId) {
        sessionsState.invalidateReferencePool(buffer.sessionId);
        void sessionsState.refreshReferencePoolSummary(buffer.sessionId).catch(() => undefined);
      }
    }
  }

  async function pollRun(runId: string) {
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      let recovery: Awaited<ReturnType<typeof getRunRecovery>>;
      try {
        const buffer = liveRunRef.current;
        const afterRevision = buffer?.runId === runId ? Number(buffer.snapshotRevision || 0) : 0;
        recovery = await getRunRecovery(runId, afterRevision);
      } catch {
        // Never treat a network failure as proof there is no active run.
        return;
      }
      if (!recovery) {
        handleUntrackableRun(runId);
        return;
      }
      const detail = recovery.run;
      await applyRunDetail(detail, recovery.terminalHydration);
      if (recovery.liveSnapshot) applyLiveSnapshot(detail, recovery.liveSnapshot);
    } finally {
      pollInFlightRef.current = false;
    }
  }

  function trackedRunIdFromRefs() {
    return liveRunRef.current?.runId
      || activeRunRef.current?.runId
      || (pendingHydrationRef.current ? recoveringRunRef.current?.id : null)
      || null;
  }

  async function recoverNow() {
    const runId = trackedRunIdFromRefs();
    if (runId) {
      await pollRun(runId);
      return;
    }
    // No tracked run: fall back to owner-scoped discovery so an ambiguous
    // local failure can still adopt a server-claimed active run.
    try {
      const active = await getActiveRun();
      if (active) adoptActiveRun(active);
    } catch {
      // Keep current state; the mount discovery retry loop owns backoff.
    }
  }

  useEffect(() => {
    let disposed = false;
    let retryTimer: number | undefined;
    const discover = async () => {
      try {
        const active = await getActiveRun();
        if (disposed) return;
        if (active) {
          adoptActiveRun(active);
        } else if (!liveRunRef.current) {
          setActiveRun(null);
        }
      } catch {
        if (!disposed) retryTimer = window.setTimeout(() => void discover(), DISCOVERY_RETRY_MS);
      }
    };
    void discover();
    return () => {
      disposed = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, []);

  const trackedRunId = liveRun?.runId || activeRun?.runId || (pendingHydration ? recoveringRun?.id : null) || null;
  // While this tab owns a healthy running SSE stream the stream is the state
  // source; once the phase leaves "running" (stop acknowledged, finalizing)
  // or the stream detaches, polling becomes the recovery engine.
  const pollingPaused = Boolean(liveRun?.abortController) && liveRun?.phase === "running";

  useEffect(() => {
    if (!trackedRunId || pollingPaused || settledFailure) return;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void pollRun(trackedRunId);
    };
    const interval = window.setInterval(tick, RUN_POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void recoverNow();
    };
    const onFocus = () => void recoverNow();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    tick();
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [trackedRunId, pollingPaused, settledFailure]);

  function beginLiveRun(buffer: LiveRunBuffer) {
    setSettledFailure(false);
    setRecoveringRun(null);
    setPendingHydration(false);
    setLiveRun((current) => {
      // A settled previous buffer in the same conversation keeps its unhydrated
      // messages visible in front of the new optimistic pair.
      if (current && current.sessionId === buffer.sessionId && current.messages.length) {
        return {
          ...buffer,
          messages: [...current.messages, ...buffer.messages],
          citationSources: mergeReferenceEntities(current.citationSources, buffer.citationSources)
        };
      }
      return buffer;
    });
  }

  function bindLiveRun(input: BindLiveRunInput) {
    setLiveRun((current) => current
      ? {
          ...current,
          runId: input.runId,
          sessionId: input.sessionId,
          projectId: input.projectId,
          phase: input.phase,
          createdAt: input.createdAt,
          messages: current.messages.map((message) => (message.runId ? message : { ...message, runId: input.runId }))
        }
      : current);
    setActiveRun({
      runId: input.runId,
      sessionId: input.sessionId,
      projectId: input.projectId,
      sessionTitle: input.sessionTitle,
      phase: input.phase,
      createdAt: input.createdAt
    });
  }

  function adoptActiveRun(run: ActiveRunSummary) {
    // A request may have committed its claim even when this tab missed the
    // run_started frame. Keep a healthy stream-owned optimistic buffer because
    // its delayed run_started callback still needs to bind and update it. Only
    // discard an unbound buffer that no active stream can finish binding.
    setSettledFailure(false);
    setPendingHydration(false);
    setRecoveringRun(null);
    setLiveRun((current) => (
      current && !current.runId && !(current.source === "stream" && current.abortController)
        ? null
        : current
    ));
    setActiveRun(run);
  }

  function discardPendingLiveRun() {
    setLiveRun((current) => (current && !current.runId ? null : current));
  }

  const setLiveMessages: Dispatch<SetStateAction<Message[]>> = (action) => {
    setLiveRun((current) => {
      if (!current) return current;
      const messages = typeof action === "function" ? (action as (old: Message[]) => Message[])(current.messages) : action;
      return messages === current.messages ? current : { ...current, messages };
    });
  };

  const setLiveCitationSources: Dispatch<SetStateAction<ReferenceItem[]>> = (action) => {
    setLiveRun((current) => {
      if (!current) return current;
      const citationSources = typeof action === "function" ? (action as (old: ReferenceItem[]) => ReferenceItem[])(current.citationSources) : action;
      return citationSources === current.citationSources ? current : { ...current, citationSources };
    });
  };

  const setLiveEvents: Dispatch<SetStateAction<unknown[]>> = (action) => {
    setLiveRun((current) => {
      if (!current) return current;
      const events = typeof action === "function" ? (action as (old: unknown[]) => unknown[])(current.events) : action;
      if (events === current.events) return current;
      return { ...current, events: events.slice(-LIVE_RUN_EVENT_LIMIT) };
    });
  };

  function setPhase(phase: ActiveRunPhase) {
    setLiveRun((current) => (current && current.phase !== phase ? { ...current, phase } : current));
    setActiveRun((current) => (current && current.phase !== phase ? { ...current, phase } : current));
  }

  function setSessionTitle(sessionId: string, title: string) {
    setActiveRun((current) => current && current.sessionId === sessionId && current.sessionTitle !== title
      ? { ...current, sessionTitle: title }
      : current);
  }

  function detachLiveRunStream() {
    setLiveRun((current) => (current && current.abortController ? { ...current, abortController: null } : current));
  }

  function markLiveRunSettled() {
    // Locally terminal without a server claim (for example a request that
    // never produced a run): keep the buffer visible but stop blocking.
    setSettledFailure(true);
    setPendingHydration(false);
    setLiveRun((current) => (current && current.abortController ? { ...current, abortController: null } : current));
  }

  function clearLiveRunAfterHydration() {
    setLiveRun(null);
    setActiveRun(null);
    setRecoveringRun(null);
    setPendingHydration(false);
    setSettledFailure(false);
  }

  return {
    activeRun,
    recoveringRun,
    liveRun,
    visibleMessages,
    visibleCitationSources,
    visibleEvents,
    blockedByActiveRun,
    ownsVisibleRun,
    ownsVisibleStream,
    ownsVisibleStreamBuffer,
    ownsVisibleSnapshot,
    beginLiveRun,
    bindLiveRun,
    adoptActiveRun,
    discardPendingLiveRun,
    setLiveMessages,
    setLiveCitationSources,
    setLiveEvents,
    setPhase,
    setSessionTitle,
    detachLiveRunStream,
    markLiveRunSettled,
    recoverNow,
    clearLiveRunAfterHydration
  };
}
